import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/translations";

const links = [
  { to: "/", key: "home" },
  { to: "/about", key: "about" },
  { to: "/services", key: "services" },
  { to: "/pricing", key: "pricing" },
  { to: "/faq", key: "faq" },
  { to: "/blog", key: "blog" },
  { to: "/contact", key: "contact" },
];

const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex items-center font-mono text-sm" data-testid="lang-toggle">
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-2 py-1 uppercase transition-colors ${
            lang === l ? "text-bronze font-semibold" : "text-stone-fg hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-sand/80 border-b border-stone" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center">
          <img src="/brand/logo-horizontal-400.png" alt="Marina Knows" className="h-8 md:h-9 w-auto" data-testid="brand-logo" />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {links.map(({ to, key }) => (
            <NavLink
              key={key}
              to={to}
              data-testid={`nav-${key}`}
              className={({ isActive }) =>
                `text-sm transition-colors hover:text-bronze ${
                  isActive ? "text-bronze font-medium" : "text-ink/70"
                }`
              }
            >
              {t.nav[key]}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <LangToggle />
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink text-sm font-medium px-5 py-2.5 rounded-full transition-colors"
          >
            <MessageCircle size={16} aria-hidden />
            {t.nav.cta}
          </a>
        </div>

        <div className="flex lg:hidden items-center gap-3">
          <LangToggle />
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="p-2 text-ink"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-stone bg-sand/95 backdrop-blur-xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map(({ to, key }) => (
                <NavLink
                  key={key}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${key}`}
                  className={({ isActive }) =>
                    `py-2.5 text-base border-b border-stone/60 transition-colors ${
                      isActive ? "text-bronze font-medium" : "text-ink/80"
                    }`
                  }
                >
                  {t.nav[key]}
                </NavLink>
              ))}
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-cta"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-terracotta text-ink font-medium px-5 py-3 rounded-full"
              >
                <MessageCircle size={16} aria-hidden />
                {t.nav.cta}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
